<script>
    import { tweened } from "svelte/motion";
    import { cubicOut } from "svelte/easing";
    import { createEventDispatcher } from "svelte";

    const dispatch = createEventDispatcher();

    // Props
    export let items = [];
    export let title = "FEATURED ITEMS";

    // State
    let currentIndex = 2;
    let carouselElement;
    let isDragging = false;
    let startX = 0;
    let maxDragDist = 0;
    let isAnimating = false;
    let direction = 0;

    const offset = tweened(0, {
        duration: 400,
        easing: cubicOut,
    });

    $: visibleItems = getVisibleItems(items, currentIndex, $offset, direction);

    function getVisibleItems(allItems, center, animOffset, dir) {
        if (allItems.length === 0) return [];
        const len = allItems.length;
        const rotationOffset = center + animOffset * dir;
        return allItems.map((item, i) => {
            const rel = (i - rotationOffset + len) % len;
            const pos = rel <= len / 2 ? rel : rel - len;
            return {
                ...item,
                position: pos,
                isCenter: Math.abs(pos) < 0.5,
                isVisible: Math.abs(pos) < 3,
            };
        });
    }

    // All visual properties computed continuously from position —
    // no class-based thresholds, no discrete snaps mid-animation.
    function itemStyle(position) {
        const abs = Math.abs(position);
        const scale     = Math.max(0.45, 1.15 - 0.3  * abs);
        const opacity   = Math.max(0.15, 1    - 0.2  * abs);
        const rotateY   = position * 28;
        const translateZ = Math.max(40,  250  - 60   * abs);
        const translateX = position * 80;
        const translateY = -20 * Math.max(0, 1 - abs * 1.5);
        const rotateZ   = position * -3; // slight tilt for physical swipe feel
        const grayscale = Math.min(0.7, Math.max(0, (abs - 1.2) * 0.7));
        const zIndex    = Math.round(10 - abs * 2);
        return [
            `--scale: ${scale}`,
            `--opacity: ${opacity}`,
            `--ry: ${rotateY}deg`,
            `--tz: ${translateZ}px`,
            `--tx: ${translateX}px`,
            `--ty: ${translateY}px`,
            `--rz: ${rotateZ}deg`,
            `--grayscale: ${grayscale}`,
            `--z: ${zIndex}`,
        ].join('; ');
    }

    async function moveCarousel(dir) {
        if (isAnimating) return;
        isAnimating = true;
        direction = dir;
        await offset.set(1);
        currentIndex = (currentIndex + dir + items.length) % items.length;
        direction = 0;
        offset.set(0, { duration: 0 });
        isAnimating = false;
    }

    const moveNext = () => moveCarousel(1);
    const movePrev = () => moveCarousel(-1);

    async function goToItem(index) {
        if (isAnimating || index === currentIndex) return;
        const forwardDistance  = (index - currentIndex + items.length) % items.length;
        const backwardDistance = (currentIndex - index + items.length) % items.length;
        const distance = Math.min(forwardDistance, backwardDistance);
        const dir = forwardDistance <= backwardDistance ? 1 : -1;
        isAnimating = true;
        direction = dir;
        await offset.set(1, { duration: Math.min(600, Math.max(300, 250 * distance)) });
        currentIndex = index;
        direction = 0;
        offset.set(0, { duration: 0 });
        isAnimating = false;
    }

    function handleDragStart(e) {
        if (isAnimating) return;
        isDragging = true;
        maxDragDist = 0;
        startX = e.type === "touchstart" ? e.touches[0].pageX : e.pageX;
    }

    function handleDragMove(e) {
        if (!isDragging || isAnimating) return;
        const x = e.type === "touchmove" ? e.touches[0].pageX : e.pageX;
        const dist = x - startX;
        maxDragDist = Math.max(maxDragDist, Math.abs(dist));
        const dragDir = dist < 0 ? 1 : -1;
        direction = dragDir;
        offset.set(Math.min(Math.abs(dist) / 150, 0.9), { duration: 0 });
        if (Math.abs(dist) > 80) {
            isDragging = false;
            isAnimating = true;
            offset.set(1, { duration: 200 }).then(() => {
                currentIndex = (currentIndex + dragDir + items.length) % items.length;
                direction = 0;
                offset.set(0, { duration: 0 });
                isAnimating = false;
            });
        }
    }

    function handleDragEnd() {
        if (!isDragging) return;
        isDragging = false;
        if (maxDragDist < 5) {
            const center = visibleItems.find(item => item.isCenter);
            if (center && center.slug) dispatch('select', center.slug);
        }
        offset.set(0, { duration: 300 });
    }
</script>

<div class="carousel-container">
    <div class="carousel-navigation">
        <button class="nav-btn" on:click={movePrev}>
            <span class="arrow">&#10094;</span>
        </button>

        <div
            class="carousel"
            bind:this={carouselElement}
            on:mousedown={handleDragStart}
            on:mouseup={handleDragEnd}
            on:mousemove={handleDragMove}
            on:touchstart={handleDragStart}
            on:touchend={handleDragEnd}
            on:touchmove={handleDragMove}
        >
            {#each visibleItems.filter((item) => item.isVisible) as item (item.slug)}
                <div class="carousel-item" style={itemStyle(item.position)} class:clickable={item.isCenter && item.slug}>
                    <img src={item.image} alt={item.title} />
                </div>
            {/each}
        </div>

        <button class="nav-btn" on:click={moveNext}>
            <span class="arrow">&#10095;</span>
        </button>
    </div>

    <div class="carousel-dots">
        {#each items as _, i}
            <button
                class="dot"
                class:active={i === currentIndex}
                on:click={() => goToItem(i)}
            ></button>
        {/each}
    </div>
</div>

<style>
    .carousel-container {
        width: 100%;
        max-width: 1200px;
        margin: 0 auto;
        padding: 40px 0 20px;
        text-align: center;
        overflow-x: hidden;
        position: relative;
    }

    .carousel-navigation {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        left: 50%;
        transform: translateX(-50%);
    }

    .carousel {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 520px;
        position: relative;
        width: 100%;
        padding: 0 20px;
        overflow: visible;
        perspective: 1200px;
        touch-action: pan-y;
        user-select: none;
    }

    .carousel-item {
        position: absolute;
        will-change: transform;
        width: 240px;
        height: 360px;
        max-width: 60vw;
        max-height: 60vh;
        transform-origin: center center;
        opacity: var(--opacity);
        z-index: var(--z);
        filter: grayscale(var(--grayscale));
        transform:
            rotateY(var(--ry))
            translateZ(var(--tz))
            translateX(var(--tx))
            translateY(var(--ty))
            rotateZ(var(--rz))
            scale(var(--scale));
    }

    .carousel-item.clickable {
        cursor: pointer;
    }

    .carousel-item img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 8px;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    }

    .item-title {
        position: absolute;
        bottom: -40px;
        left: 0;
        right: 0;
        width: 50vw;
        text-align: center;
        font-size: 1.8rem;
        font-weight: bold;
        letter-spacing: 1px;
        text-transform: uppercase;
    }

    .nav-btn {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
        z-index: 10;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        margin: 0 20px;
    }

    .nav-btn:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }

    .carousel-dots {
        display: flex;
        justify-content: center;
        margin-top: 50px;
        gap: 8px;
    }

    .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #ccc;
        border: none;
        padding: 0;
        cursor: pointer;
    }

    .dot.active {
        background-color: #000;
        width: 10px;
        height: 10px;
    }

    /* On mobile: drop 3D transforms, keep only translateX + tilt + scale */
    @media (max-width: 768px) {
        .carousel-container {
            max-width: 100vw;
        }

        .carousel {
            perspective: none;
            min-height: 380px;
        }

        .carousel-item {
            transform:
                translateX(var(--tx))
                rotateZ(var(--rz))
                scale(var(--scale));
        }
    }
</style>
